import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  App,
  Button,
  Card,
  Flex,
  Form,
  Switch,
  theme,
  Tooltip,
  Typography
} from 'antd'
import { CircleHelp } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  type OrganizationAuthMethod,
  type OrganizationAuthMethodsResponse,
  type OrganizationAuthMethodUpdate,
  organizationsOrganizationsAuthMethodsRetrieve,
  organizationsOrganizationsAuthMethodsPartialUpdate,
  organizationsOrganizationsRetrieveQueryKey
} from 'src/api/main'
import { useInvalidatePredicateQueries } from 'src/hooks'
import { useShowApiErrors } from 'src/utils'

import { AuthMethodsFieldset } from './AuthMethodsFieldset'
import { buildAuthMethodsPayload } from './authMethodsPayload'
import { useStyles } from './styles'

const { Title, Text } = Typography

const LEGACY_LOCALSTORAGE_KEY_PREFIX = 'workspace-auth-customization:'

export const organizationAuthMethodsQueryKey = (
  organizationId: number
): readonly [string, number] => ['organization-auth-methods', organizationId]

const purgeLegacyAuthStorage = () => {
  if (typeof window === 'undefined') {
    return
  }
  try {
    const keysToRemove: string[] = []
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i)
      if (key && key.startsWith(LEGACY_LOCALSTORAGE_KEY_PREFIX)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((key) => window.localStorage.removeItem(key))
  } catch {
    // Storage access can throw in private/locked contexts — non-fatal.
  }
}

type Props = {
  organizationId: number
  organizationDomainName?: string
}

const toAuthMethodsList = (
  authMethods:
    | OrganizationAuthMethodsResponse
    | { results?: OrganizationAuthMethod[] }
    | OrganizationAuthMethod[]
    | undefined
): OrganizationAuthMethod[] => {
  if (Array.isArray(authMethods)) {
    return authMethods
  }

  if (authMethods && 'auth_methods' in authMethods) {
    return authMethods.auth_methods ?? []
  }

  return authMethods?.results ?? []
}

const ADDITIONAL_SETTINGS = [
  'enforce_sso',
  'allow_self_service_join',
  'auto_provision_users'
] as const

type AdditionalSettingKey = (typeof ADDITIONAL_SETTINGS)[number]

const ADDITIONAL_SETTING_LABEL_KEY: Record<AdditionalSettingKey, string> = {
  enforce_sso: 'enforceSso',
  allow_self_service_join: 'allowSelfServiceJoin',
  auto_provision_users: 'autoProvisionUsers'
}

const ADDITIONAL_SETTING_HINT_KEY: Record<AdditionalSettingKey, string> = {
  enforce_sso: 'enforceSsoHint',
  allow_self_service_join: 'allowSelfServiceJoinHint',
  auto_provision_users: 'autoProvisionUsersHint'
}

export const OrganizationAuthMethodsCard = ({ organizationId }: Props) => {
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const { t } = useTranslation()
  const { styles } = useStyles()
  const { token } = theme.useToken()
  const queryClient = useQueryClient()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const [form] = Form.useForm()

  useEffect(() => {
    purgeLegacyAuthStorage()
  }, [])

  const { data: authMethods, isLoading } = useQuery({
    queryKey: organizationAuthMethodsQueryKey(organizationId),
    queryFn: async () => {
      const { data } = await organizationsOrganizationsAuthMethodsRetrieve({
        path: { id: organizationId }
      })

      return data
    },
    enabled: organizationId > 0
  })

  const { mutate: patchAuthMethods, isPending } = useMutation({
    mutationFn: (body: {
      auth_methods: OrganizationAuthMethodUpdate[]
      enforce_sso?: boolean
      allow_self_service_join?: boolean
      auto_provision_users?: boolean
    }) =>
      organizationsOrganizationsAuthMethodsPartialUpdate({
        path: { id: organizationId },
        body
      }),
    onSuccess: () => {
      message.success(t('information_updated_success'))
      invalidateQueries([
        organizationsOrganizationsRetrieveQueryKey({
          path: { id: organizationId }
        })
      ])
      queryClient.invalidateQueries({
        queryKey: organizationAuthMethodsQueryKey(organizationId)
      })
    },
    onError: (error) => showApiErrors(error)
  })
  const authMethodsList = useMemo(
    () =>
      toAuthMethodsList(
        authMethods as
          | OrganizationAuthMethodsResponse
          | { results?: OrganizationAuthMethod[] }
          | OrganizationAuthMethod[]
          | undefined
      ),
    [authMethods]
  )

  const additionalSettings = useMemo(() => {
    const envelope = authMethods as OrganizationAuthMethodsResponse | undefined

    return {
      enforce_sso: envelope?.enforce_sso ?? false,
      allow_self_service_join: envelope?.allow_self_service_join ?? false,
      auto_provision_users: envelope?.auto_provision_users ?? false
    }
  }, [authMethods])

  useEffect(() => {
    if (!authMethodsList.length) {
      return
    }
    const values: Record<string, unknown> = {}
    authMethodsList.forEach((m) => {
      values[`${m.method}_enabled`] = m.enabled ?? false
      values[`${m.method}_client_id`] = m.client_id ?? ''
      values[`${m.method}_client_secret`] = ''
      values[`${m.method}_tenant_id`] = m.tenant_id ?? ''
      values[`${m.method}_key_id`] = (m as Record<string, unknown>).key_id ?? ''
    })
    ADDITIONAL_SETTINGS.forEach((key) => {
      values[key] = additionalSettings[key]
    })
    form.setFieldsValue(values)
  }, [authMethodsList, additionalSettings, form])

  const onFinish = () => {
    const values = form.getFieldsValue() as Record<string, unknown>
    const payload = buildAuthMethodsPayload(values)
    patchAuthMethods({
      auth_methods: payload,
      enforce_sso: Boolean(values.enforce_sso),
      allow_self_service_join: Boolean(values.allow_self_service_join),
      auto_provision_users: Boolean(values.auto_provision_users)
    })
  }

  if (!organizationId) {
    return null
  }

  return (
    <Card
      variant="outlined"
      className={styles.card}
      loading={isLoading}
      actions={[
        <Flex justify="end" key="actions" gap={12}>
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={isPending}
          >
            {t('save')}
          </Button>
        </Flex>
      ]}
    >
      <Flex gap={32} className={styles.container}>
        <Flex orientation="vertical" gap={4} className={styles.titleContainer}>
          <Title level={4}>{t('authMethods.title')}</Title>
          <Text type="secondary" className={styles.description}>
            {t('authMethods.description')}
          </Text>
        </Flex>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          className="w-full"
        >
          <Flex orientation="vertical" gap={24}>
            <Card
              size="small"
              variant="borderless"
              className={styles.authInnerCard}
            >
              <Flex orientation="vertical" gap={16}>
                <div>
                  <Title level={5} style={{ margin: 0 }}>
                    {t('authMethods.existingProvidersTitle')}
                  </Title>
                  <Text type="secondary">
                    {t('authMethods.existingProvidersDescription')}
                  </Text>
                </div>

                <Flex orientation="vertical" gap={12}>
                  <Text strong>
                    {t('authMethods.builtInProvidersSubtitle')}
                  </Text>
                  <AuthMethodsFieldset maskedSecrets={authMethodsList} />
                </Flex>
              </Flex>
            </Card>

            <Card
              size="small"
              variant="borderless"
              className={styles.authInnerCard}
            >
              <Flex orientation="vertical" gap={12}>
                <Title level={5} style={{ margin: 0 }}>
                  {t('authMethods.additionalSettings')}
                </Title>
                <Text type="secondary">
                  {t('authMethods.additionalSettingsDescription')}
                </Text>

                {ADDITIONAL_SETTINGS.map((key) => (
                  <Flex
                    key={key}
                    justify="space-between"
                    align="center"
                    style={{ marginBottom: 8 }}
                  >
                    <Flex align="center" gap={6}>
                      <Text>
                        {t(`authMethods.${ADDITIONAL_SETTING_LABEL_KEY[key]}`)}
                      </Text>
                      <Tooltip
                        title={t(
                          `authMethods.${ADDITIONAL_SETTING_HINT_KEY[key]}`
                        )}
                      >
                        <CircleHelp
                          size={14}
                          color={token.colorTextSecondary}
                          style={{ cursor: 'help' }}
                        />
                      </Tooltip>
                    </Flex>
                    <Form.Item name={key} valuePropName="checked" noStyle>
                      <Switch />
                    </Form.Item>
                  </Flex>
                ))}
              </Flex>
            </Card>
          </Flex>
        </Form>
      </Flex>
    </Card>
  )
}
